import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { useOrg } from "@/lib/orgContext";
import type { TrackId } from "@/lib/tourTracks";

export interface OnboardingStatus {
  totalCoaches: number;
  sampleCoaches: number;
  hasSampleData: boolean;
  welcomeDismissedAt: string | null;
  tourCompletedAt: string | null;
}

interface OnboardingContextValue {
  status: OnboardingStatus | null;
  loading: boolean;
  refetch: () => Promise<void>;
  dismissWelcome: () => Promise<void>;
  completeTour: () => Promise<void>;
  loadSampleData: () => Promise<{ ok: boolean; error?: string }>;
  clearSampleData: () => Promise<{ ok: boolean; error?: string }>;
  startTour: (track?: TrackId) => void;
  stopTour: () => void;
  tourActive: boolean;
  tourTrack: TrackId;
}

const OnboardingContext = createContext<OnboardingContextValue>({
  status: null,
  loading: true,
  refetch: async () => {},
  dismissWelcome: async () => {},
  completeTour: async () => {},
  loadSampleData: async () => ({ ok: false }),
  clearSampleData: async () => ({ ok: false }),
  startTour: () => {},
  stopTour: () => {},
  tourActive: false,
  tourTrack: "quick",
});

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();
  const { org } = useOrg();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [tourActive, setTourActive] = useState(false);
  const [tourTrack, setTourTrack] = useState<TrackId>("quick");

  const refetch = useCallback(async () => {
    if (!org) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/onboarding/status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStatus(await res.json() as OnboardingStatus);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [getToken, org]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const updatePrefs = useCallback(async (body: { dismissWelcome?: boolean; completeTour?: boolean }) => {
    try {
      const token = await getToken();
      const res = await fetch("/api/onboarding/preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json() as { welcomeDismissedAt: string | null; tourCompletedAt: string | null };
        setStatus((prev) => prev ? { ...prev, ...data } : prev);
      }
    } catch { /* ignore */ }
  }, [getToken]);

  const dismissWelcome = useCallback(() => updatePrefs({ dismissWelcome: true }), [updatePrefs]);
  const completeTour = useCallback(() => updatePrefs({ completeTour: true }), [updatePrefs]);

  const loadSampleData = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch("/api/onboarding/sample-data", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        return { ok: false, error: body.error ?? `Server error ${res.status}` };
      }
      await updatePrefs({ dismissWelcome: true });
      await refetch();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : "Network error" };
    }
  }, [getToken, refetch, updatePrefs]);

  const clearSampleData = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch("/api/onboarding/sample-data", {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        return { ok: false, error: body.error ?? `Server error ${res.status}` };
      }
      await refetch();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : "Network error" };
    }
  }, [getToken, refetch]);

  const startTour = useCallback((track: TrackId = "quick") => {
    setTourTrack(track);
    setTourActive(true);
  }, []);
  const stopTour = useCallback(() => {
    setTourActive(false);
    completeTour();
  }, [completeTour]);

  return (
    <OnboardingContext.Provider
      value={{
        status,
        loading,
        refetch,
        dismissWelcome,
        completeTour,
        loadSampleData,
        clearSampleData,
        startTour,
        stopTour,
        tourActive,
        tourTrack,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  return useContext(OnboardingContext);
}
